import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type ReactNode } from "react";

import { activeBrand } from "../../shared/brand";
import type { OpsPermission } from "../../shared/operations-schemas";
import { ApiClientError } from "../api/client-core";
import { BrandMark } from "../components/Brand";
import { ContentLoading } from "../components/ContentLoading";
import { OperationsPrefetchProvider } from "./OperationsPrefetch";
import { Button } from "../components/ui/button";
import { FieldError } from "../components/ui/field";
import { cn } from "../lib/utils";
import { getOpsMe, logoutOps, opsGoogleSignInHref } from "./api";
import { AuditView } from "./AuditView";
import { AiAccessView } from "./AiAccessView";
import { parseCustomerRoute } from "./customer-route";
import { CustomerIndex } from "./CustomerIndex";
import { CustomerWorkspace } from "./CustomerWorkspace";
import { EmployeesView } from "./EmployeesView";
import { DocsWorkspace } from "./DocsWorkspace";
import { OperationsShell } from "./OperationsShell";
import { PAGE_WIDTH } from "./page-width";
import { ProvisioningView } from "./ProvisioningView";
import { SupportDesk } from "./SupportDesk";
import { useOperationsNavigation, type OperationsNavigate } from "./navigation";

function OperationsLogin() {
  const failure = new URLSearchParams(window.location.search).get("error");
  return (
    <main className="grid min-h-screen bg-background lg:grid-cols-[minmax(20rem,42%)_minmax(0,1fr)]">
      <div
        aria-label={activeBrand.name}
        className="relative flex min-h-[42dvh] items-center justify-center overflow-hidden bg-primary-deep px-8 py-12 text-primary-foreground lg:min-h-screen"
        role="img"
      >
        <div aria-hidden="true" className="absolute inset-x-8 top-8 flex items-center gap-4">
          <span className="size-1.5 bg-primary-foreground" />
          <span className="h-px flex-1 bg-primary-foreground/20" />
        </div>
        <BrandMark className="size-24 text-primary-foreground lg:size-28" />
        <div
          aria-hidden="true"
          className="absolute inset-x-8 bottom-8 h-px bg-primary-foreground/20"
        />
      </div>
      <div className="flex items-center justify-center px-6 py-12 sm:px-10 lg:px-16">
        <section aria-labelledby="operations-login-title" className="w-full max-w-[22rem]">
          <h1 className="sr-only" id="operations-login-title">
            {activeBrand.name}
          </h1>
          {failure ? (
            <div
              className="mb-6 rounded-sm border border-destructive/40 bg-destructive-background p-3 text-left text-sm text-destructive"
              role="alert"
            >
              This Google account is not authorized. Try another account or ask an administrator for
              access.
            </div>
          ) : null}
          <Button
            className="w-full"
            size="lg"
            onClick={() => {
              window.location.assign(opsGoogleSignInHref());
            }}
          >
            Sign in
          </Button>
        </section>
      </div>
    </main>
  );
}

function OperationsMain({
  navigate,
  path,
  permissions,
  currentEmployeeId,
}: {
  navigate: OperationsNavigate;
  path: string;
  permissions: ReadonlySet<OpsPermission>;
  currentEmployeeId: string;
}) {
  const support = supportContent({ navigate, path, permissions });
  if (support) return support;
  const customerRoute = parseCustomerRoute(path);
  const fixedContent = fixedOperationsContent({
    currentEmployeeId,
    navigate,
    path,
    permissions,
  });
  if (fixedContent) return fixedContent;
  if (customerRoute) {
    return (
      <CustomerWorkspace navigate={navigate} permissions={permissions} route={customerRoute} />
    );
  }
  return (
    <CustomerIndex
      canCreate={permissions.has("customer.create") && permissions.has("customer.provision")}
      canSearchPeople={permissions.has("organization.read")}
      navigate={navigate}
    />
  );
}

/** ADR-0089: the support desk, for employees who may read support tickets. */
function supportContent(input: {
  navigate: OperationsNavigate;
  path: string;
  permissions: ReadonlySet<OpsPermission>;
}): ReactNode | null {
  const isSupport = input.path === "/support" || input.path.startsWith("/support/");
  if (!isSupport || !input.permissions.has("support.ticket.read")) return null;
  return (
    <SupportDesk
      path={input.path}
      navigate={input.navigate}
      canWrite={input.permissions.has("support.ticket.write")}
      canManage={input.permissions.has("support.automation.manage")}
    />
  );
}

function fixedOperationsContent(input: {
  currentEmployeeId: string;
  navigate: OperationsNavigate;
  path: string;
  permissions: ReadonlySet<OpsPermission>;
}): ReactNode | null {
  if (input.path === "/provisioning") return <ProvisioningView />;
  if (input.path === "/ai-access") return <AiAccessView />;
  const isDocs = input.path === "/docs" || input.path.startsWith("/docs/");
  if (isDocs && input.permissions.has("docs.read")) {
    const pageId =
      input.path === "/docs" ? null : (input.path.slice("/docs/".length).split("/", 1)[0] ?? null);
    return <DocsWorkspace navigate={input.navigate} pageId={pageId} />;
  }
  if (input.path === "/audit" && input.permissions.has("audit.read")) return <AuditView />;
  if (input.path === "/employees" && input.permissions.has("permissions.manage")) {
    return <EmployeesView currentEmployeeId={input.currentEmployeeId} />;
  }
  return null;
}

function OperationsWorkspace({
  displayName,
  employeeId,
  employeePermissions,
  onSignedOut,
}: {
  displayName: string;
  employeeId: string;
  employeePermissions: OpsPermission[];
  onSignedOut: () => void;
}) {
  const queryClient = useQueryClient();
  const { navigate, path } = useOperationsNavigation();
  const permissions = new Set(employeePermissions);
  const logout = useMutation({
    mutationFn: logoutOps,
    onSuccess: () => {
      onSignedOut();
      queryClient.clear();
      window.history.replaceState(null, "", "/login");
    },
  });
  return (
    <OperationsPrefetchProvider canReadDocs={permissions.has("docs.read")}>
      <OperationsShell
        canAudit={permissions.has("audit.read")}
        canDocs={permissions.has("docs.read")}
        canManageEmployees={permissions.has("permissions.manage")}
        canSupport={permissions.has("support.ticket.read")}
        customerSelected={parseCustomerRoute(path) !== null}
        displayName={displayName}
        isSigningOut={logout.isPending}
        {...(logout.error ? { signOutError: "Could not sign out. Please try again." } : {})}
        navigate={navigate}
        onLogout={() => {
          if (
            !window.dispatchEvent(new Event("das:operations:before-navigate", { cancelable: true }))
          )
            return;
          logout.mutate();
        }}
        path={path}
      >
        {logout.error ? <FieldError>Could not sign out. Please try again.</FieldError> : null}
        <OperationsMain
          currentEmployeeId={employeeId}
          navigate={navigate}
          path={path}
          permissions={permissions}
        />
      </OperationsShell>
    </OperationsPrefetchProvider>
  );
}

function OperationsSession({ onSignedOut }: { onSignedOut: () => void }) {
  const me = useQuery({ queryKey: ["ops", "me"], queryFn: getOpsMe, retry: false });
  if (me.isLoading) {
    return (
      <main className={cn("mx-auto p-6 sm:p-8", PAGE_WIDTH)}>
        <ContentLoading label="Loading employee operations…" variant="cards" />
      </main>
    );
  }
  if (me.error instanceof ApiClientError && me.error.status === 401) return <OperationsLogin />;
  if (me.error)
    return (
      <main className="p-8">
        <FieldError>{me.error.message}</FieldError>
      </main>
    );
  return me.data ? (
    <OperationsWorkspace
      displayName={me.data.employee.displayName}
      employeeId={me.data.employee.id}
      employeePermissions={me.data.employee.permissions}
      onSignedOut={onSignedOut}
    />
  ) : null;
}

export function OperationsApp() {
  const [signedOut, setSignedOut] = useState(false);
  return signedOut ? (
    <OperationsLogin />
  ) : (
    <OperationsSession
      onSignedOut={() => {
        setSignedOut(true);
      }}
    />
  );
}
